// consumerPool.ts
import { createConsumer, fetchTokenFromHTML } from "@anycable/web"
import { backoffWithJitter } from "@anycable/core"

import { Consumer, Subscription, SubscriptionCallbacks
} from "@rails/actioncable"

// Use a global map so multiple bundles/HMR copies share the same consumers.
type GlobalWithAC = typeof globalThis & {
  __acConsumersByUrl__?: Map<string, Consumer>;
};
const globals = globalThis as GlobalWithAC;
if (!globals.__acConsumersByUrl__) globals.__acConsumersByUrl__ = new Map<string, Consumer>();
const consumersByUrl = globals.__acConsumersByUrl__;

// Optional: flip to true temporarily to debug creation/reuse.
const DEBUG_AC = false;

// Wraps fetchTokenFromHTML() with retry logic for resilience against
// transient failures (cold starts, brief network interruptions).
function tokenRefresherWithRetry() {
  const refresher = fetchTokenFromHTML();
  return async (transport: unknown) => {
    let lastError: Error | undefined;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await (refresher as (t: unknown) => Promise<void>)(transport);
        return;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        console.warn(`[Cable] Token refresh attempt ${attempt}/3 failed:`, lastError.message);
        if (attempt < 3) {
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
      }
    }
    throw lastError;
  };
}

// Override the protocol's maybeExpireSubscribe to keep retrying instead of
// permanently giving up after 10s. The default ActionCableProtocol sends the
// subscribe command once, retries once after 5s, then rejects with
// SubscriptionTimeoutError after another 5s — permanently killing the
// subscription with no recovery. This override converts that final expiry
// into additional retries (every 5s, up to MAX_EXTRA_RETRIES times).
// When the server finally responds with confirm_subscription, the pending
// subscription resolves normally. If the cable disconnects, protocol.reset()
// clears all pending subscriptions, so stale timers exit harmlessly.
const MAX_EXTRA_RETRIES = 6; // +2 built-in = 8 total attempts over ~40s

function patchSubscriptionRetry(consumer: Consumer): void {
  const cable = (consumer as any).cable;
  if (!cable?.protocol) return;

  const protocol = cable.protocol;
  protocol.maybeExpireSubscribe = function (
    id: number,
    identifier: string,
    retryInterval: number,
  ) {
    setTimeout(() => {
      const sub = this.pendingSubscriptions[identifier];
      if (!sub) return;
      if (sub.id !== id) return;

      sub._extraRetries = ((sub._extraRetries as number) || 0) + 1;

      if (sub._extraRetries > MAX_EXTRA_RETRIES) {
        // Exhausted retries — expire for real
        delete this.pendingSubscriptions[identifier];
        sub.reject(
          new Error(
            `Haven't received subscription ack after ${sub._extraRetries + 2} attempts for ${identifier}`,
          ),
        );
        return;
      }

      this.logger.warn(
        `no subscription ack, retry ${sub._extraRetries + 2}`,
        identifier,
      );
      this.cable.send(this.buildSubscribeRequest(identifier));
      this.maybeExpireSubscribe(id, identifier, retryInterval);
    }, retryInterval);
  };
}

export function getConsumer(cableUrl?: string): Consumer {
  const key = cableUrl ?? "__default__";
  let consumer = consumersByUrl.get(key);
  if (!consumer) {
    consumer = createConsumer(cableUrl, {
      logLevel: "debug",
      tokenRefresher: tokenRefresherWithRetry(),
      reconnectStrategy: backoffWithJitter(3000, { maxInterval: 30000 }),
      // Log perform() failures as warnings instead of throwing.
      // All current callers (form save, assistant ask/tell, microsite save)
      // discard the perform() promise, so throws become unhandled rejections.
      performFailures: "warn",
    });
    patchSubscriptionRetry(consumer);
    consumersByUrl.set(key, consumer);
    if (DEBUG_AC) console.info("[AC] createConsumer", { key, url: cableUrl });
  } else if (DEBUG_AC) {
    console.info("[AC] reuse existing Consumer", { key, url: cableUrl });
  }
  return consumer;
}


// Reuse an existing subscription if one already exists for the identifier.
// If found, patch its callbacks instead of creating a new one.
// Supports either a simple id (backward compatible) or an object with arbitrary params.
export function getSubscription(
  consumer: Consumer,
  channel: string,
  idOrParams: number | string | Record<string, unknown>,
  callbacks: SubscriptionCallbacks = {}
): Subscription {
  // Build params object: either { channel, id } for simple case or { channel, ...params } for object
  const params = typeof idOrParams === "object"
    ? { channel, ...idOrParams }
    : { channel, id: idOrParams };

  const identifier = JSON.stringify(params);

  // Note: consumer.subscriptions.subscriptions is an internal array.
  const subsArray: Subscription[] =
    (consumer.subscriptions as unknown as { subscriptions: Subscription[] }).subscriptions ?? [];

  const existing = subsArray.find((s: any) => s?.identifier === identifier) as Subscription | undefined;

  if (existing) {
    if (DEBUG_AC) console.info("[AC] reuse existing Subscription", { identifier });

    // Patch callbacks so callers can update handlers without recreating
    if (callbacks.received) (existing as any).received = callbacks.received;
    if (callbacks.connected) (existing as any).connected = callbacks.connected;
    if (callbacks.disconnected) (existing as any).disconnected = callbacks.disconnected;
    if (callbacks.rejected) (existing as any).rejected = callbacks.rejected;

    return existing;
  }

  if (DEBUG_AC) console.info("[AC] create Subscription", { identifier });
  return consumer.subscriptions.create(params, callbacks);
}


